import { useEffect, useState } from 'react';
import { ImageOff, Loader2 } from 'lucide-react';
import { getToken } from '../lib/api';

const cache = new Map<string, string>();

type Props = {
  recognitionId: number;
  imageIndex: number;
  className?: string;
  onReady?: (blobUrl: string) => void;
  onClick?: () => void;
  alt?: string;
};

export function AuthedImage({ recognitionId, imageIndex, className, onReady, onClick, alt }: Props) {
  const key = `r${recognitionId}:i${imageIndex}`;
  const [url, setUrl] = useState<string | null>(cache.get(key) ?? null);
  const [loading, setLoading] = useState(!cache.has(key));
  const [error, setError] = useState(false);

  useEffect(() => {
    if (cache.has(key)) {
      const u = cache.get(key)!;
      setUrl(u);
      onReady?.(u);
      return;
    }
    let revoked: string | null = null;
    let cancelled = false;
    (async () => {
      try {
        const token = getToken();
        const resp = await fetch(
          `/api/v1/recognitions/${recognitionId}/images/${imageIndex}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} },
        );
        if (!resp.ok) throw new Error(String(resp.status));
        const blob = await resp.blob();
        if (cancelled) return;
        const objUrl = URL.createObjectURL(blob);
        revoked = objUrl;
        cache.set(key, objUrl);
        setUrl(objUrl);
        onReady?.(objUrl);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      // 保留 cache,不 revoke,后续 mount 直接命中
      void revoked;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-slate-100 text-slate-400 ${className ?? ''}`}>
        <ImageOff className="h-5 w-5" />
      </div>
    );
  }
  if (loading || !url) {
    return (
      <div className={`flex items-center justify-center bg-slate-100 text-slate-400 ${className ?? ''}`}>
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={alt ?? ''}
      className={className}
      onClick={onClick}
      loading="lazy"
    />
  );
}

export function getCachedImageUrl(recognitionId: number, imageIndex: number): string | null {
  return cache.get(`r${recognitionId}:i${imageIndex}`) ?? null;
}
